import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { Injectable } from '@angular/core';
import {
    isEmpty,
    noop
} from 'lodash';
import {
    DatiqGraphXYTypes,
    IDatiqGraph,
    IDatiqGraphSeries
} from '../models';
import { DatiqAmcharts5AxisUtilsService } from './datiq-amcharts-5-axis-utils.service';
import { DatiqAmCharts5SeriesLineBuilder } from './datiq-amcharts-5-series-line.builder';
import { datiqAmcharts5SetColor } from './models';

@Injectable({
    providedIn: 'root',
})
export class DatiqAmcharts5SeriesLineService {
    constructor(private _utils: DatiqAmcharts5AxisUtilsService) {}

    create(
        root: am5.Root,
        chart: am5xy.XYChart,
        seriesData: IDatiqGraphSeries,
        settings: IDatiqGraph
    ): am5xy.XYChart {
        const series: am5xy.LineSeries = chart?.series.push(
            am5xy.LineSeries.new(root, {
                ...this.createIntialLineSettings(seriesData, chart),
            })
        );

        this.createLineSettings(series, seriesData, root, settings, chart)
            .setData(seriesData, series, chart)
            .setDefaultStroke(series, settings);

        return chart;
    }
    setDefaultStroke(series: am5xy.LineSeries, settings: IDatiqGraph): this {
        const strokeWidth =
            settings?.xyType === DatiqGraphXYTypes.Miniature ? 1 : 2;
        series?.strokes?.template?.setAll({
            strokeWidth,
        });

        return this;
    }

    createLineSettings(
        series: am5xy.LineSeries,
        seriesData: IDatiqGraphSeries,
        root: am5.Root,
        graph: IDatiqGraph,
        chart: am5xy.XYChart
    ): this {
        const builder: DatiqAmCharts5SeriesLineBuilder =
            new DatiqAmCharts5SeriesLineBuilder()
                .setRanged(seriesData)
                .setColor(seriesData)
                .setDash(seriesData, series)
                .updateSeries(series)
                .setAreaColor(series, seriesData)
                .setBullets(series, seriesData, root);

        !!seriesData.showTooltip
            ? builder.setTooltip(series, root, seriesData, graph)
            : noop();

        return this;
    }

    private createIntialLineSettings(
        seriesData: IDatiqGraphSeries,
        chart: am5xy.XYChart
    ): am5xy.ILineSeriesSettings {
        const defaultSettings: am5xy.ILineSeriesSettings = {
            name: seriesData.id,
            xAxis: chart.xAxes.getIndex(seriesData.xAxisIndex ?? 0),
            yAxis: chart.yAxes.getIndex(0),
            valueYField: 'y',
            valueXField: 'x',
            categoryXField: 'x',
            locationX: 0.5,
        };

        return !isEmpty(seriesData.ranged)
            ? {
                  ...defaultSettings,
                  valueYField: 'y',
                  openValueYField: 'yClose',
                  fill: datiqAmcharts5SetColor(seriesData.color),
              }
            : { ...defaultSettings };
    }

    private setData(
        seriesData: IDatiqGraphSeries,
        series: am5xy.LineSeries,
        chart: am5xy.XYChart
    ) {
        const data: unknown[] = this._utils.mapDataList(seriesData.data);

        series.data.setAll(data);
        const xAxis = chart.xAxes.getIndex(seriesData?.xAxisIndex ?? 0);
        this.setXAxisData(data, xAxis);

        return this;
    }
    private setXAxisData(
        data: unknown[],
        xAxis: am5xy.Axis<am5xy.AxisRenderer>
    ) {
        const mergedData: unknown[] = this._utils.mergeXAxisData(
            data,
            xAxis.data
        );
        xAxis.data.setAll(mergedData);
    }
}
